// ProfilePage — port of profile.html. View/Edit/Save/Cancel with field locking, country→timezone +
// phone-prefix cascade, avatar upload (validate type/size → POST), and password change/set (live
// checklist). Field names/logic match the Flask form; gender/country/timezone use the shared Select.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';
import { Button, Modal, Select, type SelectOption } from '@/components/ui';
import PasswordRequirements from '@/components/form/PasswordRequirements';
import Spinner from '@/components/ui/Spinner/Spinner';
import { profileService } from '@/services/api';
import { countries, countryTimezones, phonePrefixes, mobilePlaceholders } from '@/data/profileData';
import { passwordMeetsAllRequirements } from '@/lib/password';
import { getAuthErrorMessage } from '@/lib/authErrors';
import type { Gender } from '@/types';
import '@/styles/pages/profile.css';
import '@/styles/pages/settings.css';

const GENDER_OPTIONS: SelectOption[] = [
  { value: '', label: 'Select Gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const COUNTRY_OPTIONS: SelectOption[] = [
  { value: '', label: 'Select your country' },
  ...countries.map((c) => ({ value: c.code, label: c.name })),
];

// Strip a known country prefix (and non-digits) to show just the 10-digit local number.
function extractPhoneNumber(full: string): string {
  if (!full) return '';
  let n = full.trim();
  const sortedPrefixes = Object.values(phonePrefixes).sort((a, b) => b.length - a.length);
  for (const prefix of sortedPrefixes) {
    if (n.startsWith(prefix + ' ') || n.startsWith(prefix)) {
      n = n.substring(prefix.length).trim();
      break;
    }
  }
  return n.replace(/\D/g, '').slice(0, 10);
}

function normalizeAvatarUrl(url?: string | null): string {
  if (!url) return '/images/profile.png';
  if (url.startsWith('/profile/image/')) return `/api${url}`;
  return url;
}

export default function ProfileTab() {
  const { currentUser } = useAuth();
  const { success, error } = useToast();
  const profile = currentUser?.profile ?? {};

  const [editMode, setEditMode] = useState(false);
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [gender, setGender] = useState<string>(profile.gender ?? '');
  const [country, setCountry] = useState(profile.country ?? '');
  const [timezone, setTimezone] = useState(profile.timezone ?? '');
  const [mobile, setMobile] = useState(extractPhoneNumber(profile.mobileNumber ?? ''));
  const original = useRef<Record<string, string>>({});

  const [avatar, setAvatar] = useState(() => normalizeAvatarUrl(profile.avatar));

  useEffect(() => {
    if (profile.avatar) {
      setAvatar(normalizeAvatarUrl(profile.avatar));
    }
  }, [profile.avatar]);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const hasPassword = currentUser?.hasPassword ?? true;
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdMsg, setPwdMsg] = useState<{ success: boolean; text: string } | null>(null);
  const [pwdLoading, setPwdLoading] = useState(false);

  const tzOptions: SelectOption[] = useMemo(() => {
    const list = country ? (countryTimezones[country] ?? []) : [];
    return [
      { value: '', label: 'Select a timezone' },
      ...list.map(([v, l]) => ({ value: v, label: l })),
    ];
  }, [country]);

  const phonePrefix = (country && phonePrefixes[country]) || '+1';
  const mobilePlaceholder = (country && mobilePlaceholders[country]) || 'Your Mobile Number';

  const enterEdit = () => {
    original.current = { firstName, lastName, gender, country, timezone, mobile };
    setEditMode(true);
  };
  const cancelEdit = () => {
    const o = original.current;
    setFirstName(o.firstName ?? '');
    setLastName(o.lastName ?? '');
    setGender(o.gender ?? '');
    setCountry(o.country ?? '');
    setTimezone(o.timezone ?? '');
    setMobile(o.mobile ?? '');
    setEditMode(false);
  };

  const onCountryChange = (code: string) => {
    setCountry(code);
    // Auto-select the only timezone if a country has exactly one.
    const list = countryTimezones[code] ?? [];
    setTimezone(list.length === 1 ? list[0][0] : '');
    setMobile((cur) => extractPhoneNumber(cur));
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    setMobile(digits);
  };

  const handleMobilePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const extracted = extractPhoneNumber(pasted);
    const digits = (extracted || pasted).replace(/\D/g, '').slice(0, 10);
    setMobile(digits);
  };

  const save = async () => {
    try {
      const cleanMobile = mobile.replace(/\D/g, '').slice(0, 10);
      if (cleanMobile && cleanMobile.length !== 10) {
        error('Invalid Phone Number', 'Mobile number must be exactly 10 digits.');
        return;
      }
      const completeMobile = cleanMobile ? `${phonePrefix} ${cleanMobile}` : '';
      const result = await profileService.updateProfile({
        first_name: firstName,
        last_name: lastName,
        gender,
        country,
        timezone,
        mobile_number: completeMobile,
      });
      if (result.success) {
        if (currentUser) {
          useAuthStore.getState().setUser({
            ...currentUser,
            profile: {
              ...currentUser.profile,
              firstName,
              lastName,
              gender: (gender as Gender) || undefined,
              country,
              timezone,
              mobileNumber: completeMobile,
            },
          });
        }
        success('Profile Updated', 'Your profile has been updated successfully.');
        setEditMode(false);
      } else {
        error('Update Failed', result.message || 'Failed to update profile.');
      }
    } catch {
      error('Error', 'An error occurred while updating your profile.');
    }
  };

  const onDeleteImage = async () => {
    setDeletingImage(true);
    try {
      const res = await profileService.deleteImage();
      if (res.success) {
        setAvatar('/images/profile.png');
        if (currentUser) {
          useAuthStore.getState().setUser({
            ...currentUser,
            profile: {
              ...currentUser.profile,
              avatar: undefined,
            },
          });
        }
        success('Profile photo removed', 'Your profile photo has been reset to default.');
        setPreviewOpen(false);
      } else {
        error('Delete failed', res.message || 'Failed to remove profile photo.');
      }
    } catch {
      error('Error', 'An error occurred while removing your profile photo.');
    } finally {
      setDeletingImage(false);
    }
  };

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      error('Invalid file type', 'Please upload a PNG, JPG, JPEG, GIF, or WebP image.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      error('File too large', 'File size exceeds the 5MB limit. Please choose a smaller image.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const data = await profileService.uploadImage(file);
      if (data.success && data.image_url) {
        const rawUrl = data.image_url.startsWith('/profile/image/')
          ? `/api${data.image_url}`
          : data.image_url;
        setAvatar(`${rawUrl}?t=${Date.now()}`);
        if (currentUser) {
          useAuthStore.getState().setUser({
            ...currentUser,
            profile: {
              ...currentUser.profile,
              avatar: rawUrl,
            },
          });
        }
        success('Profile image updated successfully!', '');
      } else {
        error('Upload failed', data.message || 'Error uploading image.');
      }
    } catch {
      error('Error', 'Error uploading image. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const changePassword = async () => {
    setPwdMsg(null);
    if (!newPassword.trim()) {
      setPwdMsg({
        success: false,
        text: hasPassword
          ? 'Please provide both current and new passwords.'
          : 'Please enter a password to set.',
      });
      return;
    }
    if (hasPassword && !oldPassword.trim()) {
      setPwdMsg({ success: false, text: 'Please provide your current password.' });
      return;
    }
    if (!passwordMeetsAllRequirements(newPassword)) {
      setPwdMsg({
        success: false,
        text: 'Please meet all password requirements before continuing.',
      });
      return;
    }
    setPwdLoading(true);
    try {
      const data = await profileService.changePassword({
        new_password: newPassword,
        ...(hasPassword ? { old_password: oldPassword } : {}),
      });
      setPwdMsg({ success: data.success, text: data.message });
      if (data.success) {
        setOldPassword('');
        setNewPassword('');
      }
    } catch (err) {
      setPwdMsg({
        success: false,
        text: getAuthErrorMessage(err, 'Unable to change password. Please try again.'),
      });
    } finally {
      setPwdLoading(false);
    }
  };

  const inputCls = (extra = '') => `profile-input${editMode ? ' editable' : ''}${extra}`;

  const avatarNode = (
    <div className="profile-avatar">
      <button
        type="button"
        className="btn-unstyled profile-avatar-open"
        onClick={() => setPreviewOpen(true)}
        title="Click to view or change profile photo"
        aria-label="View or change profile photo"
      >
        <img
          id="profileImageDisplay"
          src={avatar}
          alt=""
          className="profile-image"
          onError={() => {
            if (avatar !== '/images/profile.png') {
              setAvatar('/images/profile.png');
            }
          }}
        />
      </button>
      <button
        type="button"
        className="profile-image-upload-label"
        title="Click to upload profile image"
        aria-label="Upload profile image"
        onClick={(e) => {
          e.stopPropagation();
          fileRef.current?.click();
        }}
      >
        <i className="bi bi-camera" />
      </button>
      <input
        ref={fileRef}
        type="file"
        id="profileImageInput"
        accept="image/*"
        className="d-none"
        aria-label="Choose a profile photo"
        onChange={onPickImage}
      />
      {uploading && (
        <div className="image-upload-progress">
          <Spinner size="sm" />
        </div>
      )}
    </div>
  );

  return (
    <div className="settings-profile">
      <section className="settings-card">
        <header className="settings-card__head">
          <div>
            <h2 className="settings-card__title">Profile Details</h2>
            <p className="settings-card__sub">
              Keep your contact information up to date to ensure seamless access and communication.
            </p>
          </div>
          {!editMode ? (
            <Button variant="primary" pill onClick={enterEdit}>
              Edit Profile
            </Button>
          ) : (
            <div className="settings-card__actions settings-card__actions--head">
              <Button variant="secondary" pill onClick={cancelEdit}>
                Cancel
              </Button>
              <Button variant="primary" pill onClick={save}>
                Save Changes
              </Button>
            </div>
          )}
        </header>

        <div className="settings-card__body settings-profile-fields">
          <div className="settings-profile-identity">
            {avatarNode}
            <div className="settings-field">
              <label className="profile-label" htmlFor="profileFirstName">
                First Name
              </label>
              <input
                type="text"
                id="profileFirstName"
                className={inputCls()}
                value={firstName}
                placeholder="Your First Name"
                readOnly={!editMode}
                disabled={!editMode}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="settings-field">
              <label className="profile-label" htmlFor="profileLastName">
                Last Name
              </label>
              <input
                type="text"
                id="profileLastName"
                className={inputCls()}
                value={lastName}
                placeholder="Your Last Name"
                readOnly={!editMode}
                disabled={!editMode}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="settings-field">
            <label className="profile-label" htmlFor="profileEmail">
              Email Address
            </label>
            <input
              type="email"
              id="profileEmail"
              className="profile-input non-editable"
              value={currentUser?.email ?? ''}
              placeholder="Email Address"
              readOnly
              disabled
            />
          </div>
          <div className="settings-field">
            <label className="profile-label" htmlFor="profileUsername">
              Username
            </label>
            <input
              type="text"
              id="profileUsername"
              className="profile-input non-editable"
              value={currentUser?.username ?? ''}
              placeholder="Username"
              readOnly
              disabled
            />
          </div>
          <div className="settings-field">
            {/* a11y (Phase 35): Select captions are <span>s (label[for] cannot target the
                  custom combobox); each Select is named via aria-labelledby instead. */}
            <span className="profile-label" id="profileGenderLabel">
              Gender
            </span>
            <Select
              value={gender}
              onChange={setGender}
              options={GENDER_OPTIONS}
              disabled={!editMode}
              buttonClassName="profile-input"
              aria-labelledby="profileGenderLabel"
              portal
            />
          </div>
          <div className="settings-field">
            <span className="profile-label" id="profileCountryLabel">
              Country
            </span>
            <Select
              value={country}
              onChange={onCountryChange}
              options={COUNTRY_OPTIONS}
              disabled={!editMode}
              buttonClassName="profile-input"
              aria-labelledby="profileCountryLabel"
              portal
            />
          </div>
          <div className="settings-field">
            <label className="profile-label" htmlFor="mobileNumber">
              Mobile Number
            </label>
            <div className="settings-phone">
              <span className="settings-phone__prefix" id="phonePrefix">
                {phonePrefix}
              </span>
              <input
                type="tel"
                className={inputCls()}
                id="mobileNumber"
                value={mobile}
                placeholder={mobilePlaceholder}
                readOnly={!editMode}
                disabled={!editMode}
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]{10}"
                onChange={handleMobileChange}
                onPaste={handleMobilePaste}
              />
            </div>
            {editMode && mobile.length > 0 && mobile.length < 10 && (
              <small className="settings-field__hint" data-testid="mobile-warning">
                Phone number must be 10 digits ({mobile.length}/10)
              </small>
            )}
          </div>
          <div className="settings-field">
            <span className="profile-label" id="profileTimezoneLabel">
              Time Zone
            </span>
            <Select
              value={timezone}
              onChange={setTimezone}
              options={tzOptions}
              disabled={!editMode}
              buttonClassName="profile-input"
              aria-labelledby="profileTimezoneLabel"
              portal
            />
          </div>
        </div>
      </section>

      <section className="settings-card profile-security-section">
        <header className="settings-card__head">
          <div>
            <h2 className="settings-card__title">Security</h2>
            <p className="settings-card__sub">Manage your password and keep your account secure.</p>
          </div>
        </header>
        <div className="settings-card__body settings-profile-fields">
          {pwdMsg && (
            <div
              className={`profile-notice profile-notice--password${
                pwdMsg.success ? ' is-success' : ' is-danger'
              }`}
            >
              {pwdMsg.text}
            </div>
          )}

          {!hasPassword && (
            <div className="profile-notice profile-notice--google">
              <i className="bi bi-google google-brand-icon" />
              Your account was created with Google. Set a password to also sign in with email &amp;
              password.
            </div>
          )}

          {hasPassword && (
            <div className="settings-field">
              <label className="profile-label" htmlFor="old_password">
                Current Password
              </label>
              <input
                type="password"
                className="profile-input editable"
                id="old_password"
                placeholder="Enter current password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
            </div>
          )}
          <div className="settings-field">
            <label className="profile-label" htmlFor="new_password">
              {hasPassword ? 'New Password' : 'Set Password'}
            </label>
            <input
              type="password"
              className="profile-input editable"
              id="new_password"
              placeholder={hasPassword ? 'Enter new password' : 'Choose a password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordRequirements password={newPassword} id="profile-password-reqs" />
          </div>
          <div className="settings-card__actions">
            <Button
              variant="primary"
              pill
              onClick={changePassword}
              disabled={pwdLoading}
              loading={pwdLoading}
            >
              {hasPassword ? 'Change Password' : 'Set Password'}
            </Button>
          </div>
        </div>
      </section>
      {/* Photo Preview Modal */}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Profile Photo"
        size="md"
      >
        <div className="profile-preview-modal-body">
          <div className="profile-preview-img-wrap">
            <img
              src={avatar}
              alt="Profile full preview"
              className="profile-preview-img"
              onError={() => {
                if (avatar !== '/images/profile.png') {
                  setAvatar('/images/profile.png');
                }
              }}
            />
          </div>
          <div className="profile-preview-actions">
            <Button
              variant="primary"
              pill
              loading={uploading}
              onClick={() => {
                fileRef.current?.click();
              }}
            >
              <i className="bi bi-camera me-1" /> Change Photo
            </Button>
            {avatar !== '/images/profile.png' && (
              <Button variant="danger" loading={deletingImage} onClick={onDeleteImage} pill>
                <i className="bi bi-trash me-1" /> Delete Photo
              </Button>
            )}
            <Button variant="secondary" pill onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
